import { CheckoutReturnLoading } from 'features/ed-redesign/CheckoutReturnPresentation';
import RedesignedQuestionnairePage from 'features/ed-redesign/ed-questionnaire-page';
import ScrollToTop from 'components/core/scroll-to-top';
import JourneyLayout from 'features/ed-redesign/JourneyLayout';
import JourneyLoading from 'features/ed-redesign/JourneyLoading';
import { ED_REDESIGN_ENABLED } from 'features/ed-redesign/config';
import BaseAppLayout from 'components/core/base-app-layout';
import SimpleBaseAppLayout from 'components/core/simple-base-app-layout';
import BaseErrorBoundaryHandler from 'components/core/base-error-boundary-handler';
import { lazy, Suspense } from 'react';
import { createRoutesFromElements, Route, createBrowserRouter, Outlet } from 'react-router-dom';
// import ProtectedRoute from './protected-route';
import { ROUTES } from '../../config/route-configuration';
import LoadingIndicator from 'components/loading/LoadingIndicator';
import RequireAdmin from './admin/require-admin';
import GTMPageViews from 'analytics/GTMPageViews';

const HomePage = lazy(() => import('./home/home-page'));
const EDTreatmentPage = lazy(() => import('./ed-treatment/ed-treatment-page'));
// The introduction must render on navigation without flashing a lazy-route spinner.
const EDQuestonnairePage = ED_REDESIGN_ENABLED
  ? RedesignedQuestionnairePage
  : lazy(() => import('./ed-treatment/ed-questionnaire-page'));
const EDResultsPage = lazy(() =>
  ED_REDESIGN_ENABLED ? import('features/ed-redesign/ed-results-page') : import('./ed-treatment/ed-results-page')
);
const EDUpsellPage = lazy(() => import('./ed-treatment/ed-upsell-page'));
const EDCompleteOrderPage = lazy(() =>
  ED_REDESIGN_ENABLED ? import('features/ed-redesign/ed-complete-order') : import('./ed-treatment/ed-complete-order')
);
const CheckoutReturnPage = lazy(() => import('./checkout/checkout-return-page'));
const UnauthorizedPage = lazy(() => import('./unauthorized/unauthorized-page'));
const SignUpPage = lazy(() => import('./sign-in/sign-in-page'));
const AccountPage = lazy(() => import('./account/account-page'));
const AccountSubscriptionsPage = lazy(() => import('./account/account-subscriptions-page'));
const AccountDeliveriesPage = lazy(() => import('./account/account-deliveries-page'));
const AdminLoginPage = lazy(() => import('./admin/admin-login-page'));
const AdminPage = lazy(() => import('./admin/admin-page'));
const PrivacyPolicyPage = lazy(() => import('./legals/privacy-policy-page'));
const TermsAndConditionsPage = lazy(() => import('./legals/terms-and-conditions-page'));
const TermsOfSupplyPage = lazy(() => import('./legals/terms-of-supply-page'));

const GTMPageViewsWrapper = () => {
  return (
    <>
      <ScrollToTop />
      <GTMPageViews />
      <Outlet />
    </>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<GTMPageViewsWrapper />}>
      <Route element={<BaseAppLayout />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route
          path={ROUTES.HOME}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <HomePage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.EDTREATMENT}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <EDTreatmentPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.SIGNUP}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <SignUpPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.UNAUTHORIZED}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <UnauthorizedPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.PRIVACYPOLICY}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <PrivacyPolicyPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.TERMSOFUSE}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <TermsAndConditionsPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.TERMSOFSUPPLY}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <TermsOfSupplyPage />
            </Suspense>
          }
        />
      </Route>
      <Route
        element={ED_REDESIGN_ENABLED ? <JourneyLayout /> : <BaseAppLayout footerVariant="contact" />}
        errorElement={<BaseErrorBoundaryHandler />}
      >
        <Route
          path={ROUTES.CHECKOUTRETURN}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense fallback={ED_REDESIGN_ENABLED ? <CheckoutReturnLoading /> : <LoadingIndicator />}>
              <CheckoutReturnPage />
            </Suspense>
          }
        />
      </Route>
      <Route element={<BaseAppLayout footerVariant="contact" />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route
          path={ROUTES.ACCOUNT}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <AccountPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.ACCOUNTDELIVERIES}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <AccountDeliveriesPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.ACCOUNTSUBSCRIPTIONS}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <AccountSubscriptionsPage />
            </Suspense>
          }
        />
      </Route>
      {/* Simple layout routes */}
      <Route path={ROUTES.HOME} element={<SimpleBaseAppLayout />} errorElement={<BaseErrorBoundaryHandler />}>
        <Route
          path={ROUTES.ADMIN_LOGIN}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <AdminLoginPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.ADMIN}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <RequireAdmin>
                <AdminPage />
              </RequireAdmin>
            </Suspense>
          }
        />
        <Route
          path={ROUTES.EDQUESTIONNAIRE}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                ED_REDESIGN_ENABLED ? (
                  <JourneyLoading />
                ) : (
                  <div className="flex items-center justify-center py-10">
                    <LoadingIndicator />
                  </div>
                )
              }
            >
              <EDQuestonnairePage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.EDRESULTS}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                ED_REDESIGN_ENABLED ? (
                  <JourneyLoading />
                ) : (
                  <div className="flex items-center justify-center py-10">
                    <LoadingIndicator />
                  </div>
                )
              }
            >
              <EDResultsPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.EDUPSELL}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10">
                  <LoadingIndicator />
                </div>
              }
            >
              <EDUpsellPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.EDCOMPLETEORDER}
          errorElement={<BaseErrorBoundaryHandler />}
          element={
            <Suspense
              fallback={
                ED_REDESIGN_ENABLED ? (
                  <JourneyLoading />
                ) : (
                  <div className="flex items-center justify-center py-10">
                    <LoadingIndicator />
                  </div>
                )
              }
            >
              <EDCompleteOrderPage />
            </Suspense>
          }
        />
      </Route>
    </Route>
  )
);

export default router;
